import {
  Add,
  ArrowDropDown,
  Check,
  Close,
  Delete,
  Edit,
} from '@mui/icons-material';
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import CustomInputField from '../../ui/primitives/CustomInputField';
import TableLoader from '../../ui/data/TableLoader';
import ActionMenu from '../../ui/primitives/ActionMenu';
import { NoModificationIcon } from '../../ui/primitives/CustomIcons';
const ACTION_SLOT_WIDTH = 40;

const EditableTable = ({
  data,
  headerTabs,
  subRowsKey = 'sub_rows',
  isLoading,
  editableFields,
  action,
  handleUpdateEntity,
  handleAddSubEntity,
  isAdmin,
  type,
  subTableName = '',
  hasSubRows,
  handleDeleteClick,
  canEdit,
  canDelete = () => true,
}) => {
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingValues, setEditingValues] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [localData, setLocalData] = useState([]);

  useEffect(() => {
    setLocalData(data);
  }, [data]);

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleEditClick = (row) => {
    setEditingRowId(row.id);
    setEditingValues({ ...row });
  };

  const handleSaveClick = async (row, parentRow = null) => {
    if (row.isNew) {
      await handleAddSubEntity(
        { name: editingValues.name, description: editingValues.description },
        parentRow
      );
      if (parentRow) {
        const updatedSubRows = (parentRow[subRowsKey] || []).filter((r) => !r.isNew);
        setLocalData((prevData) =>
          prevData.map((r) =>
            r.id === parentRow.id ? { ...r, [subRowsKey]: updatedSubRows } : r
          )
        );
      }
    } else {
      await handleUpdateEntity(row.id, editingValues, !!parentRow);
    }
    setEditingRowId(null);
    setEditingValues({});
  };

  const handleCancelClick = (parentRow) => {
    if (!parentRow) {
      setEditingRowId(null);
      setEditingValues({});
      return;
    }
    parentRow[subRowsKey] = parentRow[subRowsKey].filter((r) => !r.isNew);
    setEditingRowId(null);
    setEditingValues({});
    setLocalData([...localData]);
  };

  const handleAddBlankSubRow = (parentRow) => {
    const hasTempRow = (parentRow[subRowsKey] || []).some((r) => r.isNew);
    if (hasTempRow) return;
    const newRow = { name: '', isNew: true, parentId: parentRow.id };
    parentRow[subRowsKey] = [...(parentRow[subRowsKey] || []), newRow];
    setExpandedRows((prev) => ({ ...prev, [parentRow.id]: true }));
    setEditingRowId(newRow.id);
    setEditingValues(newRow);
    setLocalData([...localData]);
  };

  const handleInputChange = (id, value) => {
    setEditingValues((prev) => ({ ...prev, [id]: value }));
  };

  const renderCell = (row, column) => {
    const value =
      editingRowId === row.id
        ? editingValues[column.key] ?? row[column.key]
        : row[column.key];

    if (editingRowId === row.id && editableFields.includes(column.key)) {
      return (
        <CustomInputField
          id={column.key}
          value={value ?? ''}
          onChange={handleInputChange}
          placeholder={column.label}
        />
      );
    }

    if (column.key === 'color') {
      if (editingRowId === row.id) {
        return (
          <Box
            component="input"
            type="color"
            value={value}
            onChange={(e) => handleInputChange(column.key, e.target.value)}
            sx={{ width: 40, height: 32, border: '1px solid', borderColor: 'divider', borderRadius: 1, cursor: 'pointer', p: 0.25 }}
          />
        );
      }
      return (
        <Box
          sx={{
            width: 40,
            height: 28,
            backgroundColor: row.color,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        />
      );
    }

    return (
      <Typography variant="body2" color={value ? 'text.primary' : 'text.disabled'}>
        {value || '—'}
      </Typography>
    );
  };

  const renderActionButtons = (row, parentRow = null) => {
    if (!isAdmin) return null;

    const isEditable = canEdit ? canEdit(row) : true;

    // While editing: show inline Save / Cancel
    if (editingRowId === row.id) {
      return (
        <Box display="flex" justifyContent="flex-end" alignItems="center" minHeight={32}>
          <Tooltip title="Save" arrow>
            <span>
              <IconButton
                size="small"
                disabled={!editingValues.name}
                onClick={() => handleSaveClick(row, parentRow)}
                sx={{ color: 'success.main' }}
              >
                <Check sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Cancel" arrow>
            <IconButton size="small" onClick={() => handleCancelClick(parentRow)}>
              <Close sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    const menuItems = [];

    if (isEditable) {
      menuItems.push({
        label: type === 'jobs' ? "Can't be modified" : 'Edit',
        icon: <Edit />,
        onClick: () => handleEditClick(row),
        disabled: row.is_default || type === 'jobs',
      });

      if (canDelete(row)) {
        menuItems.push({
          label: 'Delete',
          icon: <Delete />,
          color: 'error',
          disabled: row.is_default,
          onClick: () => handleDeleteClick(row?.id),
        });
      }

      if (action) {
        menuItems.push({
          label: 'Delete',
          icon: <Delete />,
          color: 'error',
          disabled: row.is_default,
          onClick: () => {
            const node = action(row);
            node?.props?.onClick?.();
          },
        });
      }
    }

    return (
      <Box display="flex" justifyContent="flex-end" alignItems="center" gap={0.5}>
        {hasSubRows && !parentRow && (
          <Tooltip title={`Add ${subTableName}`} arrow>
            <IconButton
              size="small"
              onClick={() => handleAddBlankSubRow(row)}
              sx={{ color: 'primary.main' }}
            >
              <Add sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}

        {!isEditable && (
          <Box
            sx={{
              width: ACTION_SLOT_WIDTH,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Tooltip title="This value cannot be modified">
              <NoModificationIcon />
            </Tooltip>
          </Box>
        )}

        {menuItems.length > 0 && (
          <Box
            sx={{
              width: ACTION_SLOT_WIDTH,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <ActionMenu items={menuItems} tooltip="Row actions" />
          </Box>
        )}
      </Box>
    );
  };

  const colSpan = headerTabs.length + (hasSubRows ? 1 : 0) + (isAdmin ? 1 : 0);

  const renderRows = (rows, parentRow = null, level = 0) => {
    return rows.flatMap((row) => {
      const isEditing = editingRowId === row.id;

      const rowElement = (
        <TableRow
          key={row.id ?? `new-${level}`}
          selected={isEditing}
          sx={level > 0 ? { backgroundColor: 'action.hover' } : {}}
        >
          {hasSubRows && (
            <TableCell padding="checkbox">
              {row[subRowsKey]?.length > 0 && (
                <IconButton
                  size="small"
                  onClick={() => toggleRow(row.id)}
                  disableRipple
                >
                  <ArrowDropDown
                    sx={{
                      fontSize: 20,
                      transform: expandedRows[row.id] ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s',
                      color: 'text.secondary',
                    }}
                  />
                </IconButton>
              )}
            </TableCell>
          )}

          {headerTabs.map((col) => (
            <TableCell key={col.key} sx={{ pl: level > 0 ? 2 + level * 2 : undefined }}>
              {renderCell(row, col)}
            </TableCell>
          ))}

          {isAdmin && (
            <TableCell align="right" sx={{ whiteSpace: 'nowrap', width: 64, minWidth: 64, pr: 1.5 }}>
              {renderActionButtons(row, parentRow)}
            </TableCell>
          )}
        </TableRow>
      );

      const subRows =
        expandedRows[row.id] && row[subRowsKey]?.length
          ? renderRows(row[subRowsKey], row, level + 1)
          : [];

      return [rowElement, ...subRows];
    });
  };

  return (
    <TableContainer>
      <Table aria-label="editable table">
        <TableHead>
          <TableRow>
            {hasSubRows && <TableCell padding="checkbox" />}
            {headerTabs.map((tab) => (
              <TableCell key={tab.key}>{tab.label}</TableCell>
            ))}
            {isAdmin && <TableCell align="right" sx={{ width: 64, minWidth: 64, pr: 1.5 }}>Actions</TableCell>}
          </TableRow>
        </TableHead>

        <TableBody>
          {isLoading ? (
            <TableLoader colSpan={colSpan} />
          ) : localData.length > 0 ? (
            renderRows(localData)
          ) : (
            <TableRow>
              <TableCell colSpan={colSpan} align="center" sx={{ py: 6, border: 'none' }}>
                <Typography variant="body2" color="text.secondary">
                  No records found.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default EditableTable;